import React, { useMemo, useState } from 'react';
import { PARTNER_CITIES } from '../../content';
import { portalCopy } from '../copy';
import type { BillingInterval, PartnerBusiness } from '../types';
import {
  formatEuro,
  getBillingQuote,
  getListingValidUntilFromActivation,
} from '../utils/billingPlans';
import { formatListingDate, getBusinessCoverPhotoUrl } from '../utils/listingPeriod';
import { PortalButton, PortalCheckbox } from './PortalUi';

const BILLING_INTERVALS: BillingInterval[] = ['monthly', 'quarterly', 'semiannual'];

type WizardStepCheckoutProps = {
  business: PartnerBusiness;
  onActivate: (interval: BillingInterval) => Promise<void>;
  onBack: () => void;
};

const WizardStepCheckout: React.FC<WizardStepCheckoutProps> = ({ business, onActivate, onBack }) => {
  const [interval, setInterval] = useState<BillingInterval>('monthly');
  const [accepted, setAccepted] = useState(false);
  const [processing, setProcessing] = useState(false);

  const cityLabel = PARTNER_CITIES.find((city) => city.id === business.cityId)?.label ?? business.cityId;
  const categoryLabel = portalCopy.wizard.basics.categories[business.category].split(' — ')[0];
  const coverUrl = getBusinessCoverPhotoUrl(business);
  const quote = useMemo(() => getBillingQuote(business.category, interval), [business.category, interval]);
  const validUntil = formatListingDate(getListingValidUntilFromActivation(new Date(), interval));
  const planCopy = portalCopy.wizard.checkout.plans[interval];

  const handleActivate = async () => {
    setProcessing(true);
    await new Promise((resolve) => setTimeout(resolve, 1200));
    await onActivate(interval);
    setProcessing(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-instrument text-3xl italic tracking-[-0.01em] text-[#2a0f2a]">
          {portalCopy.wizard.checkout.title}
        </h2>
        <p className="mt-2 font-jakarta text-[15px] leading-relaxed text-[#5a3158]">
          {portalCopy.wizard.checkout.subtitle}
        </p>
      </div>

      <div className="overflow-hidden rounded-[24px] border border-[rgba(74,33,71,0.1)] bg-white">
        <div className="relative aspect-[16/9] bg-[#f2e8f0] sm:aspect-[2/1]">
          {coverUrl ? (
            <img src={coverUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center font-jakarta text-sm text-[#5a3158]/60">
              {portalCopy.dashboard.noCoverPhoto}
            </div>
          )}
        </div>
        <div className="p-5">
          <p className="font-jakarta text-xs font-semibold uppercase tracking-wide text-[#5a3158]">
            {portalCopy.wizard.checkout.previewLabel}
          </p>
          <h3 className="mt-1 font-instrument text-2xl italic text-[#2a0f2a]">{business.name}</h3>
          <p className="mt-1 font-jakarta text-sm text-[#5a3158]">
            {categoryLabel} · {business.neighbourhood} · {cityLabel}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="font-jakarta text-sm font-bold uppercase tracking-wide text-[#5a3158]">
          {portalCopy.wizard.checkout.planTitle}
        </h3>
        <div className="grid gap-3 sm:grid-cols-3">
          {BILLING_INTERVALS.map((option) => {
            const optionQuote = getBillingQuote(business.category, option);
            const optionCopy = portalCopy.wizard.checkout.plans[option];
            const isSelected = interval === option;
            const badge = 'badge' in optionCopy ? optionCopy.badge : null;

            return (
              <button
                key={option}
                type="button"
                onClick={() => setInterval(option)}
                className={`rounded-[20px] border p-4 text-left transition-colors ${
                  isSelected
                    ? 'border-chisme-primary bg-chisme-primary/5 ring-2 ring-chisme-primary/20'
                    : 'border-[rgba(74,33,71,0.12)] bg-white hover:border-chisme-primary/30'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-jakarta text-sm font-bold text-[#2a0f2a]">{optionCopy.label}</span>
                  {badge && (
                    <span className="shrink-0 rounded-full bg-chisme-primary px-2 py-0.5 font-jakarta text-[10px] font-bold uppercase text-white">
                      {badge}
                    </span>
                  )}
                </div>
                <p className="mt-2 font-instrument text-2xl italic text-chisme-primary">
                  {formatEuro(optionQuote.effectiveMonthlyPrice)} €
                  <span className="font-jakarta text-xs font-semibold not-italic text-[#5a3158]">/mes</span>
                </p>
                {optionQuote.savings > 0 && (
                  <p className="mt-1 font-jakarta text-xs font-semibold text-chisme-primary">
                    {portalCopy.wizard.checkout.savings(formatEuro(optionQuote.savings))}
                  </p>
                )}
                <p className="mt-2 font-jakarta text-xs leading-relaxed text-[#5a3158]">{optionCopy.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-[24px] border border-[rgba(74,33,71,0.1)] bg-white p-6">
        <h3 className="font-jakarta text-sm font-bold uppercase tracking-wide text-[#5a3158]">
          {portalCopy.wizard.checkout.includesTitle}
        </h3>
        <ul className="mt-4 space-y-2">
          {portalCopy.wizard.checkout.includes.map((item) => (
            <li key={item} className="flex items-start gap-2.5 font-jakarta text-sm text-[#2a0f2a]">
              <span className="mt-0.5 text-chisme-primary" aria-hidden>
                ✓
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-4 rounded-[24px] border border-[rgba(74,33,71,0.1)] bg-white p-6">
        <h3 className="font-jakarta text-sm font-bold uppercase tracking-wide text-[#5a3158]">
          {portalCopy.wizard.checkout.summary}
        </h3>

        <dl className="space-y-3 font-jakarta text-[15px]">
          <div className="flex justify-between gap-4">
            <dt className="text-[#5a3158]">{portalCopy.wizard.checkout.plan}</dt>
            <dd className="text-right font-semibold text-[#2a0f2a]">{categoryLabel}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#5a3158]">Zona</dt>
            <dd className="text-right font-semibold text-[#2a0f2a]">
              {business.neighbourhood} · {cityLabel}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#5a3158]">{portalCopy.wizard.checkout.period}</dt>
            <dd className="text-right font-semibold text-[#2a0f2a]">{planCopy.label}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#5a3158]">{portalCopy.wizard.checkout.validUntil}</dt>
            <dd className="text-right font-semibold text-[#2a0f2a]">{validUntil}</dd>
          </div>
          <div className="flex justify-between gap-4 border-t border-[rgba(74,33,71,0.1)] pt-3">
            <dt className="text-[#5a3158]">{portalCopy.wizard.checkout.totalToday}</dt>
            <dd className="font-instrument text-2xl italic text-chisme-primary">
              {formatEuro(quote.totalPrice)} €
            </dd>
          </div>
        </dl>

        <p className="font-jakarta text-xs leading-relaxed text-[#5a3158]">
          {portalCopy.wizard.checkout.paymentNote}
        </p>

        <div className="rounded-2xl bg-[#fcf7fa] p-4">
          <PortalCheckbox
            id="checkout-terms"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            label={
              <span className="font-jakarta text-sm leading-relaxed text-[#5a3158]">
                {portalCopy.wizard.checkout.terms}
              </span>
            }
            className="items-start"
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <PortalButton variant="secondary" fullWidth onClick={onBack} disabled={processing}>
          {portalCopy.wizard.back_step}
        </PortalButton>
        <PortalButton fullWidth disabled={!accepted || processing} onClick={handleActivate}>
          {processing
            ? portalCopy.wizard.checkout.processing
            : portalCopy.wizard.checkout.activate(formatEuro(quote.totalPrice))}
        </PortalButton>
      </div>
    </div>
  );
};

export default WizardStepCheckout;
